<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useUserStore } from '../stores/userStore'
import axios from '../axios.js'

const props = defineProps({
  show: Boolean
})

const emit = defineEmits(['close'])

const userStore = useUserStore()
const currentUser = computed(() => userStore.currentUser)

// Check if user is admin
const isAdmin = computed(() => currentUser.value?.role === 'admin')

// Active tab: 'admin' or 'client'
const activeTab = ref(isAdmin.value ? 'admin' : 'client')

// Leagues data
const leagues = ref([])
const loadingLeagues = ref(false)
const selectedLeague = ref(null)

// Templates data
const templates = ref([])
const loadingTemplates = ref(false)
const selectedTemplate = ref(null)

// Search queries
const leagueSearchQuery = ref('')
const templateSearchQuery = ref('')

// Fetch leagues
const fetchLeagues = async () => {
  loadingLeagues.value = true
  try {
    // TODO: Replace with actual API endpoint
    const response = await axios.get('/api/leagues')
    leagues.value = response.data || []
  } catch (error) {
    console.error('Error fetching leagues:', error)
    leagues.value = []
  } finally {
    loadingLeagues.value = false
  }
}

// Fetch templates for selected league
const fetchTemplates = async (leagueId) => {
  if (!leagueId) return
  
  loadingTemplates.value = true
  try {
    // TODO: Replace with actual API endpoint
    // For admin on Admin tab: fetch all templates for the league
    // For admin on Client tab: fetch client-specific templates (need to select client)
    // For client: fetch only their templates for the league
    let endpoint
    if (isAdmin.value && activeTab.value === 'admin') {
      endpoint = `/api/templates/league/${leagueId}`
    } else if (isAdmin.value && activeTab.value === 'client') {
      // Admin viewing client templates - for now, show all client templates
      // TODO: Add client selection dropdown for admin
      endpoint = `/api/templates/league/${leagueId}/clients`
    } else {
      endpoint = `/api/templates/league/${leagueId}/client/${currentUser.value.id}`
    }
    
    const response = await axios.get(endpoint)
    templates.value = response.data || []
  } catch (error) {
    console.error('Error fetching templates:', error)
    templates.value = []
  } finally {
    loadingTemplates.value = false
  }
}

// Filtered leagues based on search
const filteredLeagues = computed(() => {
  if (!leagueSearchQuery.value) return leagues.value
  
  const query = leagueSearchQuery.value.toLowerCase()
  return leagues.value.filter(league => {
    const leagueName = league.league_name || league.name || ''
    return leagueName.toLowerCase().includes(query)
  })
})

// Filtered templates based on search
const filteredTemplates = computed(() => {
  if (!templateSearchQuery.value) return templates.value
  
  const query = templateSearchQuery.value.toLowerCase()
  return templates.value.filter(template => {
    const templateName = template.name || template.template_name || ''
    return templateName.toLowerCase().includes(query) ||
           template.id?.toString().includes(query)
  })
})

// Handle league selection
const selectLeague = (league) => {
  selectedLeague.value = league
  selectedTemplate.value = null
  templateSearchQuery.value = ''
  fetchTemplates(league.id || league.league_id)
}

// Handle template selection
const selectTemplate = (template) => {
  selectedTemplate.value = template
}

// Initialize on mount
onMounted(() => {
  if (props.show) {
    fetchLeagues()
  }
})

// Watch for show prop changes
watch(() => props.show, (newValue) => {
  if (newValue) {
    fetchLeagues()
    selectedLeague.value = null
    selectedTemplate.value = null
    leagueSearchQuery.value = ''
    templateSearchQuery.value = ''
  }
})

// Watch for activeTab changes (admin switching between Admin/Client tabs)
watch(activeTab, () => {
  if (selectedLeague.value) {
    // Refetch templates when switching tabs
    fetchTemplates(selectedLeague.value.id || selectedLeague.value.league_id)
    selectedTemplate.value = null
  }
})
</script>

<template>
  <div 
    v-if="show" 
    class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    @click.self="$emit('close')"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b shrink-0">
        <h2 class="text-2xl font-semibold text-gray-800">Template Management</h2>
        <button 
          @click="$emit('close')" 
          class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <i class="fas fa-times text-gray-500"></i>
        </button>
      </div>

      <!-- Tabs (Admin can see both tabs, Client only sees Client view directly) -->
      <div class="border-b px-6 shrink-0" v-if="isAdmin">
        <div class="flex space-x-6">
          <button 
            v-for="tab in ['Admin', 'Client']" 
            :key="tab"
            @click="activeTab = tab.toLowerCase()"
            :class="[
              'px-4 py-3 font-medium border-b-2 transition-colors',
              activeTab === tab.toLowerCase()
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            ]"
          >
            {{ tab }}
          </button>
        </div>
      </div>
      
      <!-- Client view header (no tabs for clients) -->
      <div v-else class="border-b px-6 shrink-0 py-3">
        <h3 class="text-lg font-medium text-gray-700">Your Templates</h3>
      </div>

      <!-- Content Area -->
      <div class="flex-1 overflow-hidden flex">
        <!-- Left Panel: Leagues List -->
        <div class="w-80 border-r bg-gray-50 flex flex-col">
          <div class="p-4 border-b">
            <h3 class="font-medium text-gray-700 mb-3">Leagues</h3>
            <div class="relative">
              <input 
                v-model="leagueSearchQuery"
                type="text" 
                placeholder="Search leagues..."
                class="w-full pl-8 pr-4 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
              <i class="fas fa-search absolute left-2.5 top-2.5 text-gray-400 text-sm"></i>
            </div>
          </div>
          
          <div class="flex-1 overflow-y-auto">
            <div v-if="loadingLeagues" class="p-4 text-center text-gray-500">
              <i class="fas fa-spinner fa-spin"></i>
              <p class="mt-2">Loading leagues...</p>
            </div>
            
            <div v-else-if="filteredLeagues.length === 0" class="p-4 text-center text-gray-500">
              <p>No leagues found</p>
            </div>
            
            <div v-else>
              <div 
                v-for="league in filteredLeagues" 
                :key="league.id || league.league_id"
                @click="selectLeague(league)"
                class="p-4 border-b cursor-pointer transition-colors"
                :class="[
                  selectedLeague?.id === league.id || selectedLeague?.league_id === league.league_id
                    ? 'bg-blue-50 border-blue-200' 
                    : 'hover:bg-gray-100'
                ]"
              >
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <i class="fas fa-basketball-ball text-gray-600"></i>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="font-medium text-gray-700 truncate">
                      {{ league.league_name || league.name }}
                    </div>
                    <div class="text-xs text-gray-500">
                      ID: {{ league.id || league.league_id }}
                    </div>
                  </div>
                  <i 
                    v-if="selectedLeague?.id === league.id || selectedLeague?.league_id === league.league_id"
                    class="fas fa-chevron-right text-blue-500"
                  ></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Middle Panel: Templates List (shown when league is selected) -->
        <div v-if="selectedLeague" class="w-80 border-r bg-gray-50 flex flex-col">
          <div class="p-4 border-b">
            <div class="flex items-center justify-between mb-3">
              <h3 class="font-medium text-gray-700">Templates</h3>
              <button 
                @click="selectedLeague = null; selectedTemplate = null"
                class="text-gray-400 hover:text-gray-600"
                title="Back to leagues"
              >
                <i class="fas fa-arrow-left"></i>
              </button>
            </div>
            <div class="text-xs text-gray-500 mb-2">
              {{ selectedLeague.league_name || selectedLeague.name }}
            </div>
            <div class="relative">
              <input 
                v-model="templateSearchQuery"
                type="text" 
                placeholder="Search templates..."
                class="w-full pl-8 pr-4 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
              <i class="fas fa-search absolute left-2.5 top-2.5 text-gray-400 text-sm"></i>
            </div>
          </div>
          
          <div class="flex-1 overflow-y-auto">
            <div v-if="loadingTemplates" class="p-4 text-center text-gray-500">
              <i class="fas fa-spinner fa-spin"></i>
              <p class="mt-2">Loading templates...</p>
            </div>
            
            <div v-else-if="filteredTemplates.length === 0" class="p-4 text-center text-gray-500">
              <p>No templates found</p>
              <button 
                class="mt-2 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Create Template
              </button>
            </div>
            
            <div v-else>
              <div 
                v-for="template in filteredTemplates" 
                :key="template.id"
                @click="selectTemplate(template)"
                class="p-4 border-b cursor-pointer transition-colors"
                :class="[
                  selectedTemplate?.id === template.id
                    ? 'bg-blue-50 border-blue-200' 
                    : 'hover:bg-gray-100'
                ]"
              >
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <i class="fas fa-file-alt text-gray-600"></i>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="font-medium text-gray-700 truncate">
                      {{ template.name || template.template_name || `Template ${template.id}` }}
                    </div>
                    <div class="text-xs text-gray-500">
                      ID: {{ template.id }}
                    </div>
                  </div>
                  <i 
                    v-if="selectedTemplate?.id === template.id"
                    class="fas fa-chevron-right text-blue-500"
                  ></i>
                </div>
              </div>
            </div>
          </div>
          
          <div class="p-4 border-t">
            <button 
              class="w-full px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
            >
              <i class="fas fa-plus"></i>
              Create New Template
            </button>
          </div>
        </div>

        <!-- Right Panel: Template Details (shown when template is selected) -->
        <div v-if="selectedTemplate" class="flex-1 bg-white flex flex-col">
          <div class="p-6 border-b">
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-lg font-semibold text-gray-800">
                  {{ selectedTemplate.name || selectedTemplate.template_name || `Template ${selectedTemplate.id}` }}
                </h3>
                <p class="text-sm text-gray-500 mt-1">
                  Template ID: {{ selectedTemplate.id }}
                </p>
              </div>
              <button 
                @click="selectedTemplate = null"
                class="text-gray-400 hover:text-gray-600"
                title="Back to templates"
              >
                <i class="fas fa-arrow-left"></i>
              </button>
            </div>
          </div>
          
          <div class="flex-1 overflow-y-auto p-6">
            <div class="space-y-4">
              <div class="bg-gray-50 rounded-lg p-4">
                <h4 class="font-medium text-gray-700 mb-2">Template Information</h4>
                <div class="space-y-2 text-sm">
                  <div class="flex justify-between">
                    <span class="text-gray-500">Template ID:</span>
                    <span class="text-gray-900 font-medium">{{ selectedTemplate.id }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-gray-500">Name:</span>
                    <span class="text-gray-900">{{ selectedTemplate.name || selectedTemplate.template_name || '-' }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-gray-500">League:</span>
                    <span class="text-gray-900">{{ selectedLeague.league_name || selectedLeague.name }}</span>
                  </div>
                </div>
              </div>
              
              <div class="bg-gray-50 rounded-lg p-4">
                <h4 class="font-medium text-gray-700 mb-2">Template Settings</h4>
                <p class="text-sm text-gray-500">Template configuration will be displayed here</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State: No template selected -->
        <div v-else-if="selectedLeague && !selectedTemplate" class="flex-1 bg-white flex items-center justify-center">
          <div class="text-center text-gray-500">
            <i class="fas fa-file-alt text-4xl mb-4"></i>
            <p>Select a template to view details</p>
          </div>
        </div>

        <!-- Empty State: No league selected -->
        <div v-else class="flex-1 bg-white flex items-center justify-center">
          <div class="text-center text-gray-500">
            <i class="fas fa-basketball-ball text-4xl mb-4"></i>
            <p>Select a league to view templates</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Hide scrollbar for Chrome, Safari and Opera */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>

